import {
  Calendar,
  ChevronDown,
  ChevronUp,
  Download,
  ExternalLink,
  Eye,
  MessageSquare,
} from "lucide-react";
import React, { useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { Notification } from "../types";
import { Button } from "./ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

interface NotificationListProps {
  notifications: Notification[];
  loading: boolean;
  searchTerm?: string;
}

type SortField = "title" | "departamento" | "subdepartamento" | "fecha";
type SortDirection = "asc" | "desc";

const NotificationList: React.FC<NotificationListProps> = ({
  notifications,
  loading,
  searchTerm,
}) => {
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [expandedNotification, setExpandedNotification] = useState<
    number | null
  >(null);
  const { darkMode } = useTheme();

  // Función para manejar el ordenamiento
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Si ya estamos ordenando por este campo, cambiamos la dirección
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Si es un nuevo campo, ordenamos ascendente por defecto
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Ordenar notificaciones
  const sortedNotifications = [...notifications].sort((a, b) => {
    if (!sortField) return 0;

    const fieldA = a[sortField] || "";
    const fieldB = b[sortField] || "";

    if (sortDirection === "asc") {
      return String(fieldA).localeCompare(String(fieldB));
    } else {
      return String(fieldB).localeCompare(String(fieldA));
    }
  });

  // Renderizar icono de ordenamiento
  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return null;

    return sortDirection === "asc" ? (
      <ChevronUp className="h-4 w-4 inline-block ml-1" />
    ) : (
      <ChevronDown className="h-4 w-4 inline-block ml-1" />
    );
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "Sin fecha";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  };

  // Función para resaltar coincidencias en el texto
  const highlightText = (text: string, searchTerm: string) => {
    if (!searchTerm || searchTerm === "undefined" || !text) return text;

    // Escapar caracteres especiales en el término de búsqueda
    const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedSearchTerm})`, "gi");

    // Dividir el texto por las coincidencias y mapear cada parte
    const parts = text.split(regex);

    return parts.map((part, index) =>
      part.match(regex) ? (
        <span
          key={index}
          className={`${
            darkMode
              ? "bg-yellow-700 font-semibold"
              : "bg-yellow-200 font-semibold"
          }`}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  // Función para truncar texto largo
  const truncateText = (text: string, maxLength: number = 150) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Función para formatear texto con saltos de línea
  const formatTextWithLineBreaks = (text: string, searchTerm: string) => {
    if (!text) return null;

    // Dividir el texto por saltos de línea
    const lines = text.split("\n");

    // Mapear cada línea y aplicar el resaltado de búsqueda
    return lines.map((line, index) => (
      <React.Fragment key={index}>
        {highlightText(line, searchTerm)}
        {index < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  // Función para manejar la expansión de una notificación
  const toggleExpand = (id: number) => {
    if (expandedNotification === id) {
      setExpandedNotification(null);
    } else {
      setExpandedNotification(id);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-muted-foreground">Cargando notificaciones...</p>
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="p-8 text-center">
        <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-lg font-medium mb-2">
          No se encontraron notificaciones
        </h3>
        <p className="text-muted-foreground">
          Prueba a cambiar los filtros de búsqueda
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead
              className="w-[40%] cursor-pointer"
              onClick={() => handleSort("title")}
            >
              Título {renderSortIcon("title")}
            </TableHead>
            <TableHead
              className="w-[20%] cursor-pointer"
              onClick={() => handleSort("departamento")}
            >
              Departamento {renderSortIcon("departamento")}
            </TableHead>
            <TableHead
              className="w-[20%] cursor-pointer"
              onClick={() => handleSort("subdepartamento")}
            >
              Subdepartamento {renderSortIcon("subdepartamento")}
            </TableHead>
            <TableHead
              className="w-[10%] cursor-pointer"
              onClick={() => handleSort("fecha")}
            >
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                Fecha {renderSortIcon("fecha")}
              </div>
            </TableHead>
            <TableHead className="w-[10%] text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedNotifications.map((notification) => (
            <React.Fragment key={notification.id}>
              <TableRow className="hover:bg-muted/50">
                <TableCell className="font-medium">
                  <div className="flex items-center">
                    <div>
                      {highlightText(
                        truncateText(notification.title),
                        searchTerm || ""
                      )}
                    </div>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="ml-2"
                          onClick={() => toggleExpand(notification.id)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          <span>Ver texto</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Ver texto completo</TooltipContent>
                    </Tooltip>
                  </div>
                </TableCell>
                <TableCell>{notification.departamento}</TableCell>
                <TableCell>{notification.subdepartamento}</TableCell>
                <TableCell>{formatDate(notification.fecha)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end space-x-2">
                    {notification.pdf_url && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              window.open(notification.pdf_url, "_blank")
                            }
                          >
                            <ExternalLink className="h-4 w-4" />
                            <span className="sr-only">Ver PDF</span>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Ver PDF</TooltipContent>
                      </Tooltip>
                    )}
                    {notification.pdf_url && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              window.open(notification.pdf_url, "_blank")
                            }
                          >
                            <Download className="h-4 w-4" />
                            <span className="sr-only">Descargar PDF</span>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Descargar PDF</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </TableCell>
              </TableRow>
              {expandedNotification === notification.id && (
                <TableRow>
                  <TableCell colSpan={5} className="bg-muted/30 p-4">
                    <div className="space-y-4">
                      <h4 className="font-semibold text-center">Título:</h4>
                      <div className="text-justify mx-auto max-w-4xl px-8">
                        <div className="text-center">
                          {formatTextWithLineBreaks(
                            notification.title,
                            searchTerm || ""
                          )}
                        </div>
                      </div>
                      {notification.pdf_text && (
                        <>
                          <h4 className="font-semibold mt-6 text-center">
                            Contenido del PDF:
                          </h4>
                          <div className="max-h-96 overflow-y-auto p-6 bg-background text-foreground border border-border rounded-md text-sm mx-auto max-w-4xl">
                            <div className="text-justify px-4">
                              {formatTextWithLineBreaks(
                                notification.pdf_text,
                                searchTerm || ""
                              )}
                            </div>
                          </div>
                        </>
                      )}
                      {notification.id_boe && (
                        <p className="text-sm text-muted-foreground mt-4 text-center">
                          ID: {notification.id_boe}
                        </p>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default NotificationList;
